import { memo, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from '@aimsos/i18n';
import { Button, FormMultiSelect, FormSelect, SearchableSelect } from '@aimsos/ui';
import { Plus, X } from 'lucide-react';

import { AGGREGATE_WITHOUT_FIELD, WIDGET_AGGREGATES, WIDGET_FIELD_ROLES } from '../constants/widgetContract.js';
import { columnFitsSlotModel } from '../utils/widgetSlotModel.js';
import { countsRows, toItems, withItem } from '../utils/slotAssignment.js';
import BindingSlotLabel from './BindingSlotLabel.jsx';

/**
 * One binding slot of the chosen type, and everything it needs (CT-5725).
 *
 * The Data step publishes a plain record set, so **this is where the widget
 * is configured**: a measure slot asks for its operation and the column to
 * run it over, a dimension or a column slot asks for the column. Both read
 * the type's own rules — `x-aggregates` for the operations it accepts,
 * `x-data-types` (narrowed by the chosen operation) for the columns — so an
 * author cannot pick something that fails three clicks later at publish.
 *
 * A slot the contract declares as an array is a list of the same thing: a
 * list of measures for `y[]` or `metrics[]`, a list of columns for
 * `columns[]` or `identity[]`, capped at its `maxItems`.
 */

/** The operations a measure slot offers: its own, or the full set. */
const aggregatesFor = (slot) => (slot.aggregates?.length ? slot.aggregates : WIDGET_AGGREGATES);

/** One measure: an operation, and the column it reads (none for a row count). */
const MeasureItem = memo(function MeasureItem({
  slot, item, columns, isLoading, onChange, onRemove, canRemove,
}) {
  const { t } = useTranslation('dashboards');

  const operations = useMemo(
    () => aggregatesFor(slot).map((value) => ({
      value,
      label: t(`widgetBuilder.measures.aggregate.${value}`, { defaultValue: value }),
    })),
    [slot, t],
  );

  const options = useMemo(
    () => columns
      .filter((column) => columnFitsSlotModel(column, slot, item.aggregate))
      .map((column) => ({ id: column.fieldUuid, name: column.displayName })),
    [columns, item.aggregate, slot],
  );

  // Changing the operation drops a column it cannot read: `SUM` takes a
  // number where `COUNT` took anything, and a stale column would publish a
  // type mismatch.
  const handleOperation = useCallback((aggregate) => onChange({
    aggregate,
    ...(aggregate === AGGREGATE_WITHOUT_FIELD ? {} : { field: item.field }),
  }), [item.field, onChange]);

  const handleColumn = useCallback(
    (option) => onChange({ ...item, field: option.id }),
    [item, onChange],
  );

  return (
    <div className="flex items-start gap-2">
      <div className="w-[9.5rem] shrink-0">
        <FormSelect
          value={item.aggregate ?? ''}
          onChange={handleOperation}
          options={operations}
          placeholder={t('widgetBuilder.measures.aggregatePlaceholder')}
          portalListbox
        />
      </div>

      {countsRows(item) ? (
        <p className="m-0 flex-1 pt-2.5 text-[11px] text-content-muted">
          {t('widgetBuilder.measures.countsRows')}
        </p>
      ) : (
        <div className="min-w-0 flex-1">
          <SearchableSelect
            options={options}
            value={item.field || null}
            onSelect={handleColumn}
            showAvatar={false}
            isLoading={isLoading}
            disabled={!item.aggregate || options.length === 0}
            placeholder={t('widgetBuilder.measures.columnPlaceholder')}
            searchPlaceholder={t('widgetBuilder.filters.columnSearchPlaceholder')}
            ariaLabel={t('widgetBuilder.measures.columnAria')}
          />
        </div>
      )}

      {canRemove ? (
        <button
          type="button"
          onClick={onRemove}
          aria-label={t('widgetBuilder.measures.removeAria')}
          className="mt-2 shrink-0 text-content-muted transition-colors hover:text-content"
        >
          <X size={16} />
        </button>
      ) : null}
    </div>
  );
});

MeasureItem.propTypes = {
  slot: PropTypes.object.isRequired,
  item: PropTypes.object.isRequired,
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  isLoading: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  canRemove: PropTypes.bool,
};

const BindingSlotRow = memo(function BindingSlotRow({
  slot, value, columns, takenOnly, isLoading, onChange,
}) {
  const { t } = useTranslation('dashboards');

  const isMeasure = slot.roles?.includes(WIDGET_FIELD_ROLES.AGGREGATE);
  const kind = isMeasure
    ? 'measure'
    : (slot.roles?.includes(WIDGET_FIELD_ROLES.DIMENSION) ? 'dimension' : 'column');

  const items = useMemo(() => toItems(value), [value]);
  const max = slot.isArray ? (slot.max ?? Infinity) : 1;

  /* The columns this slot accepts as a plain column — no operation involved. */
  const options = useMemo(
    () => columns
      .filter((column) => columnFitsSlotModel(column, slot))
      .map((column) => ({ id: column.fieldUuid, name: column.displayName })),
    [columns, slot],
  );

  const emit = useCallback((next) => {
    const kept = next.filter(Boolean);
    if (!kept.length) { onChange(slot.key, null); return; }
    onChange(slot.key, slot.isArray ? kept : kept[0]);
  }, [onChange, slot.isArray, slot.key]);

  const handleItem = useCallback(
    (index, item) => emit(withItem(value, index, item)),
    [emit, value],
  );
  const handleAdd = useCallback(() => emit([...items, {}]), [emit, items]);
  const handleMultiple = useCallback(
    (fieldUuids) => emit(fieldUuids.slice(0, max).map((field) => ({ field }))),
    [emit, max],
  );
  const handleSingle = useCallback(
    (option) => emit([{ field: option.id }]),
    [emit],
  );

  const emptyNote = takenOnly
    ? t('widgetBuilder.bindingSlots.noCandidatesTaken')
    : t(`widgetBuilder.bindingSlots.noCandidatesBy.${kind}`, {
      defaultValue: t('widgetBuilder.bindingSlots.noCandidates'),
    });
  const isEmpty = options.length === 0 && !isMeasure;

  return (
    <div className={`flex min-w-0 flex-col gap-1.5${isMeasure && slot.isArray ? ' sm:col-span-2' : ''}`}>
      <BindingSlotLabel
        slotKey={slot.key}
        role={kind}
        required={slot.required}
      />

      {isMeasure ? (
        <div className="flex flex-col gap-2">
          {(items.length ? items : [{}]).map((item, index) => (
            <MeasureItem
              // The row's place in the slot is its identity: items carry no id
              // of their own, and two measures may read the same column.
              // eslint-disable-next-line react/no-array-index-key
              key={`${slot.key}-${index}`}
              slot={slot}
              item={item}
              columns={columns}
              isLoading={isLoading}
              canRemove={items.length > 1}
              onChange={(next) => handleItem(index, next)}
              onRemove={() => handleItem(index, null)}
            />
          ))}
          {slot.isArray && items.length < max ? (
            <div>
              <Button variant="secondary" size="sm" leftIcon={Plus} onClick={handleAdd}>
                {t('widgetBuilder.measures.add')}
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}

      {!isMeasure && slot.isArray ? (
        <FormMultiSelect
          options={options.map((option) => ({ value: option.id, label: option.name }))}
          value={items.map((item) => item.field).filter(Boolean)}
          onChange={handleMultiple}
          disabled={isEmpty || isLoading}
          placeholder={t('widgetBuilder.bindingSlots.listPlaceholder')}
          /*
            The Configure panel scrolls, so an absolutely positioned list is
            clipped at its edge and the options below the fold are
            unreachable. The column and operation pickers in this same row
            already portal; this one was the odd one out (CT-5887).
          */
          portalListbox
        />
      ) : null}

      {!isMeasure && !slot.isArray ? (
        <SearchableSelect
          options={options}
          value={items[0]?.field || null}
          onSelect={handleSingle}
          showAvatar={false}
          isLoading={isLoading}
          disabled={isEmpty}
          placeholder={t(`widgetBuilder.bindingSlots.placeholderBy.${kind}`, {
            defaultValue: t('widgetBuilder.bindingSlots.placeholder'),
          })}
          searchPlaceholder={t('widgetBuilder.filters.columnSearchPlaceholder')}
          ariaLabel={slot.key}
        />
      ) : null}

      {isEmpty && !isLoading ? (
        <p className="m-0 text-[11px] text-content-muted">{emptyNote}</p>
      ) : null}

      {!isEmpty && slot.isArray && slot.max ? (
        <p className="m-0 text-[11px] text-content-muted">
          {t('widgetBuilder.bindingSlots.listHint', { max: slot.max })}
        </p>
      ) : null}
    </div>
  );
});

BindingSlotRow.propTypes = {
  slot: PropTypes.shape({
    key: PropTypes.string.isRequired,
    required: PropTypes.bool,
    isArray: PropTypes.bool,
    max: PropTypes.number,
    roles: PropTypes.arrayOf(PropTypes.string),
    aggregates: PropTypes.arrayOf(PropTypes.string),
    dataTypes: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  /** `{ field, aggregate }`, or a list of them for an array slot. */
  value: PropTypes.oneOfType([PropTypes.object, PropTypes.array, PropTypes.string]),
  /** The columns the Data step exposed, with their metadata. */
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  /** Everything that fits is already bound to another slot. */
  takenOnly: PropTypes.bool,
  isLoading: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
};

export default BindingSlotRow;
